import {
  CANVAS_PRESETS,
  exportSpecForFormat,
  flowRulesForFormat,
  formatCatalogEntry,
  MENU_FORMATS,
  syntheticLegacyVariantId,
  type CanvasSpec,
  type ExportSpec,
  type FlowRules,
  type MenuFormat,
  type PageBlueprint,
  type TemplateFormatVariant,
} from '@menuvera/engine';

import { duplicateBlueprint, type TemplateDocument } from './nodeTree';

export { exportSpecForFormat };

export function formatVariantId(format: MenuFormat, existingIds: Iterable<string>): string {
  const taken = new Set(existingIds);
  if (!taken.has(format)) return format;
  let index = 2;
  while (taken.has(`${format}-${index}`)) index += 1;
  return `${format}-${index}`;
}

export function nextUnusedFormat(used: Iterable<MenuFormat>): MenuFormat {
  const taken = new Set(used);
  return unusedFormats(taken)[0] ?? 'tv_slide';
}

export function unusedFormats(used: Iterable<MenuFormat>): MenuFormat[] {
  const taken = new Set(used);
  return MENU_FORMATS.filter((entry) => !taken.has(entry));
}

export function flowForFormat(format: MenuFormat, source?: FlowRules): FlowRules {
  return flowRulesForFormat(format, source);
}

export function isAutoFormatLabel(label: string | undefined, format: MenuFormat): boolean {
  if (!label) return true;
  const auto = format.replaceAll('_', ' ');
  const catalog = formatCatalogEntry(format).label;
  return label === auto || label === catalog;
}

export function applyFormatPreset(
  target: {
    format?: MenuFormat;
    label?: string;
    canvas: CanvasSpec;
    flow: FlowRules;
    export?: ExportSpec;
  },
  format: MenuFormat,
  previousFormat?: MenuFormat,
): void {
  const entry = formatCatalogEntry(format);
  const prior = previousFormat ?? target.format ?? format;
  if (target.label !== undefined && isAutoFormatLabel(target.label, prior)) {
    target.label = entry.label;
  }
  target.format = format;
  Object.assign(target.canvas, { ...entry.canvas });
  Object.assign(target.flow, flowRulesForFormat(format, target.flow));
  if (target.export) {
    const spec = exportSpecForFormat(format);
    target.export.kinds = spec.kinds;
    target.export.defaultKind = spec.defaultKind;
    target.export.output = spec.output;
    target.export.print = spec.print;
  }
}

export function cloneBlueprintsForVariant(blueprints: PageBlueprint[]): PageBlueprint[] {
  return blueprints.map((blueprint) => {
    const copy = duplicateBlueprint(blueprint);
    return { ...copy, label: blueprint.label, variant: blueprint.variant };
  });
}

export function legacyVariantFromDocument(document: TemplateDocument): TemplateFormatVariant {
  const format = document.format ?? 'digital_book';
  return {
    id: syntheticLegacyVariantId(format),
    format,
    label: formatCatalogEntry(format).label,
    canvas: { ...document.canvas },
    blueprints: document.blueprints,
    flow: { ...document.flow },
    export: exportSpecForFormat(format),
  };
}

export function ensureExplicitVariants(document: TemplateDocument): TemplateFormatVariant[] {
  if (document.variants?.length) return document.variants;
  return [legacyVariantFromDocument(document)];
}

export function sourceVariantForClone(
  document: TemplateDocument,
  activeVariantId: string | null,
): TemplateFormatVariant {
  return (
    document.variants?.find((variant) => variant.id === activeVariantId) ??
    document.variants?.[0] ??
    legacyVariantFromDocument(document)
  );
}

/** Builds a renderable format branch from the currently edited design. */
export function buildAddedFormatVariant(
  document: TemplateDocument,
  activeVariantId: string | null,
  format?: MenuFormat,
): TemplateFormatVariant {
  const existing = ensureExplicitVariants(document);
  const source = sourceVariantForClone(document, activeVariantId);
  const nextFormat = format ?? nextUnusedFormat(existing.map((variant) => variant.format));
  const blueprints = cloneBlueprintsForVariant(
    source.blueprints.length ? source.blueprints : document.blueprints,
  );
  if (blueprints.length === 0) {
    throw new Error('Cannot add a format variant without at least one page recipe.');
  }
  return {
    id: formatVariantId(nextFormat, existing.map((variant) => variant.id)),
    format: nextFormat,
    label: formatCatalogEntry(nextFormat).label,
    canvas: { ...CANVAS_PRESETS[nextFormat] },
    blueprints,
    flow: flowForFormat(nextFormat, source.flow),
    export: exportSpecForFormat(nextFormat),
  };
}

export function variantHasId(
  document: TemplateDocument | null | undefined,
  variantId: string | null | undefined,
): boolean {
  if (!document || !variantId) return true;
  if (!document.variants?.length) return false;
  return document.variants.some((variant) => variant.id === variantId);
}
